import {
  DeserializedRecord,
  deserializeItem,
  Deserializer,
  deserializeSingleRelationship,
  JSONAPIRecord,
  JSONAPIReference,
  JSONAPISingleRelationship
} from "../../Helpers";
import {deserializePatient, Patient} from "../../Patient";
import {deserializeGroup, Group} from "../Group";

/**
 * Labels and Identification for the ECG leads.
 *
 * The correct order is important, since the enum is used to sort the leads in the ECG detail
 */
enum Lead {
  unknown = "unknown",
  I = "I",
  II = "II",
  III = "III",
  aVR = "aVR",
  aVL = "aVL",
  aVF = "aVF",
  V1 = "V1",
  V2 = "V2",
  V3 = "V3",
  V4 = "V4",
  V5 = "V5",
  V6 = "V6"
}

enum Classification {
  unknown = "unknown",
  sinusRhythm = "sinus_rhythm",
  atrialFibrillation = "atrial_fibrillation",
  inconclusiveLowHeartRate = "inconclusive_low_heart_rate",
  inconclusiveHighHeartRate = "inconclusive_high_heart_rate",
  inconclusivePoorReading = "inconclusive_poor_reading",
  inconclusiveOther = "inconclusive_other",
  prematureVentricularContraction = "premature_ventricular_contraction"
}

enum ECGProcessingMethod {
  Neurokit2 = "neurokit2",
  Raw = "raw",
  BioSPPY = "bioSPPY"
}

enum ECGWritingSpeed {
  FiftyPerSecond = "50 mm/s",
  TwentyFivePerSecond = "25 mm/s"
}
type ClassificationType = `${Classification}`;

interface ECG extends DeserializedRecord<"ecg"> {
  isRelevant: boolean;
  measuredAt: Date;
  source: string;
  clientId: string;
  group?: Group;
  lead: Lead;
  classification: ClassificationType;
  assessment?: string,
  values: Array<number>;
  samplingFrequency: number;
  seenAt?: Date;
  seenBy?: JSONAPIReference<"physician">;
  patient?: Patient;
  fileName?: string;
  downloadUrl?: string;
  byteFileSize?: number;
}

interface ECGData extends JSONAPIRecord {
  relationships: {
    seen_by: JSONAPISingleRelationship;
    patient: JSONAPISingleRelationship;
    patient_measurement_group: JSONAPISingleRelationship;
  }
}

interface IncomingEcgCount extends DeserializedRecord<"count"> {
  notSeen: number
}

const deserializeEcgMeasurementWithPatient: Deserializer<ECG, ECGData> = (data, included) => {
  const patient = deserializeSingleRelationship<Patient>(data.relationships.patient, included, deserializePatient);

  return {
    ...deserializeEcgMeasurement(data, included),
    patient
  };
};

const deserializeEcgMeasurement: Deserializer<ECG, ECGData> = (data, included) => {
  const group = deserializeSingleRelationship(data.relationships.patient_measurement_group, included, deserializeGroup);
  const seenBy = deserializeSingleRelationship<JSONAPIReference<"physician">>(data.relationships.seen_by);

  return {
    ...deserializeItem(data),
    group,
    seenBy
  };
};

export {
  ECG,
  deserializeEcgMeasurement,
  deserializeEcgMeasurementWithPatient,
  ECGProcessingMethod,
  ECGWritingSpeed,
  Classification,
  IncomingEcgCount,
  Lead
};
